import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { showAPI, classEntryAPI, refundAPI, scoreAPI } from '../utils/api';
import { ArrowLeft, MagnifyingGlass, Funnel, PencilSimple, Trash, CurrencyDollar, X, Horse, User, CaretDown, UploadSimple, Plus, ArrowCounterClockwise, ClockCounterClockwise, ChartBar, Note, Bed, Tent, Users, Package, ListBullets, Crosshair } from '@phosphor-icons/react';
import { RiderAutocomplete } from '../components/show/RiderAutocomplete';
import { OfficeNotesTab } from '../components/manage/OfficeNotesTab';
import { StallsTab } from '../components/manage/StallsTab';
import { StaffInvoicesSection } from '../components/manage/StaffInvoicesSection';
import { AddonsTab } from '../components/manage/AddonsTab';
import { RiderChargesTab } from '../components/manage/RiderChargesTab';
import { TargetsTab } from '../components/manage/TargetsTab';
import { toast } from 'sonner';
import api from '../utils/api';
import { isSameDay } from '../utils/sameDay';

const ManageEntries = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [show, setShow] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('ALL');
  const [paymentFilter, setPaymentFilter] = useState('ALL');
  const [activeDayTab, setActiveDayTab] = useState('ALL');
  // 'TABLE' = the existing rider/class table; 'RUN_ORDER' = a flat list
  // sorted by draw number, intended for the announcer/timer at the gate.
  const [viewMode, setViewMode] = useState('TABLE');
  // Inline timing state for the Run Order view. `timingEntryId` is the row
  // currently expanded for input; the timer types a number → Enter, or hits
  // NT/SCR. After a save we auto-advance to the next un-timed row so a single
  // person at the gate can chain submissions without ever leaving this view.
  const [timingEntryId, setTimingEntryId] = useState(null);
  const [timingValue, setTimingValue] = useState('');
  const [timingKnocks, setTimingKnocks] = useState(0);
  const [timingSaving, setTimingSaving] = useState(false);
  // Top-level page tabs: 'ENTRIES' = existing entries UI; 'NOTES' = office staff notepad.
  const [activePageTab, setActivePageTab] = useState('ENTRIES');
  // Revenue report modal state
  const [reportOpen, setReportOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  // Square routing diagnostic (loaded with the revenue report)
  const [routingDiag, setRoutingDiag] = useState(null);

  const loadRoutingDiag = async () => {
    try {
      const { data } = await api.get(`/connect/square/diagnose/${id}`);
      setRoutingDiag(data);
    } catch {
      setRoutingDiag(null); // non-fatal; just hides the banner
    }
  };

  const openReport = async () => {
    setReportOpen(true);
    loadRoutingDiag();
    if (reportData) return;
    setReportLoading(true);
    try {
      const { data } = await api.get(`/shows/${id}/revenue-report`);
      setReportData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load report');
      setReportOpen(false);
    } finally {
      setReportLoading(false);
    }
  };
  const refreshReport = async () => {
    setReportLoading(true);
    loadRoutingDiag();
    try {
      const { data } = await api.get(`/shows/${id}/revenue-report`);
      setReportData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to refresh report');
    } finally {
      setReportLoading(false);
    }
  };
  const timingInputRef = useRef(null);

  // v228 — Balance Owed panel state.
  const [balanceOpen, setBalanceOpen] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceData, setBalanceData] = useState(null);

  const openBalanceOwed = async () => {
    setBalanceOpen(true);
    setBalanceLoading(true);
    try {
      const { data } = await api.get(`/shows/${id}/balance-owed`);
      setBalanceData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load balances');
      setBalanceOpen(false);
    } finally {
      setBalanceLoading(false);
    }
  };
  const [editingEntry, setEditingEntry] = useState(null);
  const [editRider, setEditRider] = useState('');
  const [editHorse, setEditHorse] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const fileRef = useRef(null);
  const [importLoading, setImportLoading] = useState(false);
  const [refundRequests, setRefundRequests] = useState([]);
  const [processingRefund, setProcessingRefund] = useState(null);
  const [directRefundConfirm, setDirectRefundConfirm] = useState(null);
  const [denyModal, setDenyModal] = useState(null);
  const [denyReason, setDenyReason] = useState('');
  // Multi-class Add Entry modal — producer registers a single rider/horse
  // across any number of classes in one submission.
  const [addModal, setAddModal] = useState(false);
  const [addRider, setAddRider] = useState('');
  const [addHorse, setAddHorse] = useState('');
  const [addClassIds, setAddClassIds] = useState(new Set());
  // Per-class options when adding from the modal: { classId: { drawNumber: '5', isRollover: true } }
  const [addClassOpts, setAddClassOpts] = useState({});
  const [addSubmitting, setAddSubmitting] = useState(false);
  // Target Race opt-in state for producer-side Add Entry (mirrors EntryForm).
  // `targets` is the full list of target races on this show; `addSelectedTargetIds`
  // is the subset the producer is opting this rider into; `addTargetCategoryPicks`
  // maps target_id -> [category_id] when a target has multiple categories.
  const [targets, setTargets] = useState([]);
  const [addSelectedTargetIds, setAddSelectedTargetIds] = useState([]);
  const [addTargetCategoryPicks, setAddTargetCategoryPicks] = useState({});
  // Inline draw # editor — click a row's draw cell, type a number, Enter to save.
  // `drawEditingId` is the entry currently being edited; `drawEditValue` is the
  // string in the input (kept as a string so we can support empty = clear).
  const [drawEditingId, setDrawEditingId] = useState(null);
  const [drawEditValue, setDrawEditValue] = useState('');
  const [drawSaving, setDrawSaving] = useState(false);
  // Rollover dropdown — `rolloverEntry` is the entry whose target picker is open.
  const [rolloverEntry, setRolloverEntry] = useState(null);
  const [rolloverSaving, setRolloverSaving] = useState(false);

  const userRoles = user?.roles || [user?.role];
  const isMaster = userRoles.some(r => ['MASTER', 'LMASTER'].includes(r));

  useEffect(() => {
    loadData();
    loadRefundRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [showRes, entriesRes] = await Promise.all([
        showAPI.get(id),
        classEntryAPI.listAll(id),
      ]);
      setShow(showRes.data);
      setEntries(entriesRes.data.entries || []);
      // Best-effort: load Target Races so the producer Add Entry modal can
      // offer opt-in checkboxes the same way the rider self-entry form does.
      try {
        const { data: tgts } = await api.get(`/shows/${id}/targets`);
        setTargets(Array.isArray(tgts) ? tgts : []);
      } catch { setTargets([]); }
    } catch (err) {
      toast.error('Failed to load show data');
    } finally {
      setLoading(false);
    }
  };

  // Build day tabs from show classes. Prefer scheduled_date when present
  // (it sorts chronologically and is unambiguous across week boundaries),
  // and fall back to scheduled_day for legacy classes.
  // NOTE: This must be defined BEFORE classNames useMemo which depends on classIdsByDay
  const { dayKeys, classIdsByDay } = useMemo(() => {
    if (!show) return { dayKeys: [], classIdsByDay: {} };
    const byDay = {};
    show.classes.forEach(cls => {
      const day = cls.scheduled_date || cls.scheduled_day || 'All Days';
      if (!byDay[day]) byDay[day] = [];
      byDay[day].push(cls.id);
    });
    const keys = Object.keys(byDay).sort((a, b) => {
      // Dates (e.g. '2026-06-05') sort lexicographically; weekdays follow
      // their natural week order.
      const order = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const ai = order.indexOf(a), bi = order.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      return a.localeCompare(b);
    });
    const isMultiDay = keys.length > 1 || (keys.length === 1 && keys[0] !== 'All Days');
    return { dayKeys: isMultiDay ? keys : [], classIdsByDay: byDay };
  }, [show]);

  const classNames = useMemo(() => {
    if (!show) return [];
    // Scope the class-filter dropdown to whatever day tab is active so producers
    // don't have to scroll past the wrong day's classes mid-show. When "All
    // Days" is active we fall back to the full class list.
    const scoped = activeDayTab === 'ALL'
      ? show.classes
      : show.classes.filter(c => (classIdsByDay[activeDayTab] || []).includes(c.id));
    return [...new Set(scoped.map(c => c.name))].sort();
  }, [show, activeDayTab, classIdsByDay]);

  // Auto-pick today's day tab when the producer opens the page during a
  // multi-day event. Avoids the "scroll past Friday's classes on Saturday"
  // problem at the gate. Only triggers once per show load — the producer can
  // still flip back to "All Days" manually.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!show || !dayKeys || dayKeys.length === 0) return;
    if (activeDayTab !== 'ALL') return; // already picked
    const today = new Date();
    const isoToday = today.toISOString().slice(0, 10);
    const weekday = today.toLocaleDateString('en-US', { weekday: 'long' });
    const showStart = (show.start_date || '').slice(0, 10);
    const showEnd = (show.end_date || '').slice(0, 10);
    const showIsActive = isoToday >= showStart && isoToday <= showEnd;
    if (!showIsActive) return;
    // Match by either scheduled_date (e.g. '2026-06-05') or scheduled_day
    // (e.g. 'Friday'). Day strings are stored in the format the picker uses,
    // so a case-insensitive equality check is enough.
    const match = dayKeys.find(d => d === isoToday || d.toLowerCase() === weekday.toLowerCase());
    if (match) setActiveDayTab(match);
  }, [show?.id, dayKeys.length]);

  // If the active day tab no longer matches any visible day (e.g. the
  // producer just removed the day's last class), fall back to All Days
  // so the filter doesn't leave the entries pane empty by accident.
  useEffect(() => {
    if (activeDayTab !== 'ALL' && dayKeys.length > 0 && !dayKeys.includes(activeDayTab)) {
      setActiveDayTab('ALL');
    }
  }, [dayKeys, activeDayTab]);

  // Build class_id to class_name map for day filtering
  const classIdToName = useMemo(() => {
    if (!show) return {};
    const map = {};
    show.classes.forEach(c => { map[c.id] = c.name; });
    return map;
  }, [show]);

  const filteredEntries = useMemo(() => {
    const matched = entries.filter(e => {
      const matchesSearch = !search ||
        e.rider_name.toLowerCase().includes(search.toLowerCase()) ||
        (e.horse_name && e.horse_name.toLowerCase().includes(search.toLowerCase()));
      const matchesClass = classFilter === 'ALL' || e.class_name === classFilter;
      const matchesPayment = paymentFilter === 'ALL' || e.payment_status === paymentFilter;
      const matchesDay = activeDayTab === 'ALL' || (classIdsByDay[activeDayTab] || []).includes(e.class_id);
      return matchesSearch && matchesClass && matchesPayment && matchesDay;
    });

    // Rollover ordering: each rollover entry slots directly below its source
    // entry (same rider+horse, source class matches `rollover_source_entry_id`
    // or `rollover_source_class_id`). Producers want to see the destination
    // class on the next row so they can trace a roll at a glance. Non-rollovers
    // keep their original order (creation time).
    //
    // Algorithm: build a map of source entry → its rollovers, then walk the
    // non-rollover list in order and emit each one followed by its rollovers
    // (recursively, in case a rollover destination is itself rolled).
    const rolloversBySource = new Map();
    const orphanRollovers = [];
    matched.forEach(e => {
      if (!e.is_rollover) return;
      const srcEntryId = e.rollover_source_entry_id;
      if (srcEntryId) {
        if (!rolloversBySource.has(srcEntryId)) rolloversBySource.set(srcEntryId, []);
        rolloversBySource.get(srcEntryId).push(e);
      } else {
        // No source entry id (older rollover, or source filtered out) —
        // append to bottom so we don't lose it.
        orphanRollovers.push(e);
      }
    });

    const out = [];
    const placed = new Set();
    const emit = (entry) => {
      if (placed.has(entry.id)) return;
      placed.add(entry.id);
      out.push(entry);
      const kids = rolloversBySource.get(entry.id) || [];
      kids.forEach(emit);
    };
    matched.forEach(e => { if (!e.is_rollover) emit(e); });
    // Any rollover whose source is in the matched list but somehow wasn't
    // attached (defensive). Then orphans.
    matched.forEach(e => { if (e.is_rollover) emit(e); });
    orphanRollovers.forEach(emit);
    return out;
  }, [entries, search, classFilter, paymentFilter, activeDayTab, classIdsByDay]);

  // Resolve the source-class name for a rollover entry. Producers want to see
  // WHICH class an entry rolled in from (e.g. "from Open Barrels") next to the
  // RO badge so they can trace the rollover at a glance without opening the
  // Rollover panel. Returns null if the entry isn't a rollover or the source
  // class has since been removed from the show.
  const rolloverSourceName = useCallback((entry) => {
    if (!entry?.is_rollover || !entry?.rollover_source_class_id) return null;
    const src = (show?.classes || []).find(c => c.id === entry.rollover_source_class_id);
    return src?.name || null;
  }, [show]);

  // Run-order view: flatten entries by (class index in show, draw_number).
  // This matches how producers actually run a barrel race — one class at a
  // time, in the order configured on the show, in draw_number order. Entries
  // without a draw number sink to the bottom of their class so the announcer
  // sees them as "to be drawn / late add".
  const runOrderRows = useMemo(() => {
    if (!show) return [];
    const classIndex = {};
    show.classes.forEach((c, i) => { classIndex[c.id] = i; });
    const scoped = filteredEntries.filter(e => activeDayTab === 'ALL' || (classIdsByDay[activeDayTab] || []).includes(e.class_id));
    return [...scoped].sort((a, b) => {
      const ca = classIndex[a.class_id] ?? 999;
      const cb = classIndex[b.class_id] ?? 999;
      if (ca !== cb) return ca - cb;
      const da = a.draw_number ?? Number.MAX_SAFE_INTEGER;
      const db = b.draw_number ?? Number.MAX_SAFE_INTEGER;
      if (da !== db) return da - db;
      return (a.rider_name || '').localeCompare(b.rider_name || '');
    });
  }, [filteredEntries, show, activeDayTab, classIdsByDay]);

  // First entry in run-order that hasn't been timed yet — the announcer's
  // "next up" row. Status COMPLETED + a final_time means done; everything
  // else (no score, NT, SCR, or PENDING) is fair game for "next".
  const nextUpId = useMemo(() => {
    const next = runOrderRows.find(e => {
      const s = e.score;
      if (!s) return true;
      return !(s.status === 'COMPLETED' && s.final_time != null);
    });
    return next?.id || null;
  }, [runOrderRows]);

  // Open the inline timer for a row + autofocus the input.
  const openTimingFor = useCallback((entryId) => {
    setTimingEntryId(entryId);
    setTimingValue('');
    setTimingKnocks(0);
    // Focus on next render so the input exists in the DOM.
    setTimeout(() => timingInputRef.current?.focus(), 0);
  }, []);

  // Reload entries after a score submission so the row's `score` payload
  // reflects the new time and the next-up calculation moves forward.
  const reloadEntries = useCallback(async () => {
    try {
      const { data } = await classEntryAPI.listAll(id);
      setEntries(data.entries || []);
    } catch {
      // ignore — the timer can keep working with stale local state
    }
  }, [id]);

  const submitTime = useCallback(async (statusOverride) => {
    if (!timingEntryId || timingSaving) return;
    const row = runOrderRows.find(r => r.id === timingEntryId);
    if (!row) return;
    let status = statusOverride || 'COMPLETED';
    let timeNum = 0;
    if (status === 'COMPLETED') {
      timeNum = parseFloat(timingValue);
      if (!Number.isFinite(timeNum) || timeNum <= 0) {
        toast.error('Enter a valid time before pressing Enter');
        return;
      }
    }
    setTimingSaving(true);
    try {
      await scoreAPI.submit({
        entry_id: row.id,
        class_id: row.class_id,
        time: timeNum,
        knocks: status === 'COMPLETED' ? Number(timingKnocks) || 0 : 0,
        status,
      });
      // Advance to the next un-timed row in the same class first; if none,
      // advance to the next un-timed row anywhere. Falls back to closing the
      // timer when the day's run order is complete.
      const idx = runOrderRows.findIndex(r => r.id === timingEntryId);
      const after = runOrderRows.slice(idx + 1);
      const nextRow = after.find(r => {
        const s = r.score;
        if (!s) return true;
        return !(s.status === 'COMPLETED' && s.final_time != null);
      });
      await reloadEntries();
      if (nextRow) {
        openTimingFor(nextRow.id);
      } else {
        setTimingEntryId(null);
        setTimingValue('');
        setTimingKnocks(0);
        toast.success('Run order complete for this day');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save time');
    } finally {
      setTimingSaving(false);
    }
  }, [timingEntryId, timingValue, timingKnocks, timingSaving, runOrderRows, reloadEntries, openTimingFor]);

  const stats = useMemo(() => {
    const paid = entries.filter(e => e.payment_status === 'PAID').length;
    const unpaid = entries.filter(e => ['PENDING', 'UNPAID'].includes(e.payment_status)).length;
    const comp = entries.filter(e => e.payment_status === 'COMP').length;
    const unpaidFees = entries.filter(e => ['PENDING', 'UNPAID'].includes(e.payment_status)).reduce((sum, e) => {
      const cls = show?.classes.find(c => c.id === e.class_id);
      return sum + (cls ? parseFloat(cls.entry_fee || 0) : 0);
    }, 0);
    return { total: entries.length, paid, unpaid, comp, unpaidFees };
  }, [entries, show]);

  const handleTogglePayment = async (entry) => {
    const cycle = ['UNPAID', 'PAID', 'COMP'];
    const current = entry.payment_status || 'UNPAID';
    const next = cycle[(cycle.indexOf(current) + 1) % cycle.length];
    try {
      await classEntryAPI.updatePayment(id, entry.class_id, entry.id, next);
      setEntries(prev => prev.map(e => e.id === entry.id ? { ...e, payment_status: next } : e));
    } catch {
      toast.error('Failed to update payment');
    }
  };

  const handleToggleTieOutWaive = async (entry) => {
    if (!entry?.rider_id) {
      toast.error('Rider account required for tie-out waiver');
      return;
    }
    const nextWaived = !entry.tie_out_waived;
    try {
      await api.post(`/shows/${id}/riders/${entry.rider_id}/waive-tie-out`, { waived: nextWaived });
      // Update local state for ALL of this rider's pending entries in this show.
      setEntries(prev => prev.map(e => (
        e.rider_id === entry.rider_id && e.payment_status !== 'PAID' && e.payment_status !== 'COMP'
          ? { ...e, tie_out_waived: nextWaived }
          : e
      )));
      toast.success(nextWaived ? 'Tie-out fee waived for this rider' : 'Tie-out fee re-billed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update tie-out');
    }
  };

  const handleStartEdit = (entry) => {
    setEditingEntry(entry);
    setEditRider(entry.rider_name);
    setEditHorse(entry.horse_name || '');
  };

  const handleSaveEdit = async () => {
    if (!editRider.trim()) { toast.error('Rider name is required'); return; }
    try {
      await classEntryAPI.update(id, editingEntry.class_id, editingEntry.id, {
        rider_name: editRider.trim(),
        horse_name: editHorse.trim(),
      });
      setEntries(prev => prev.map(e => e.id === editingEntry.id ? { ...e, rider_name: editRider.trim(), horse_name: editHorse.trim() } : e));
      setEditingEntry(null);
      toast.success('Entry updated');
    } catch {
      toast.error('Failed to update entry');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const resp = await classEntryAPI.remove(id, deleteConfirm.class_id, deleteConfirm.id);
      const cascaded = (resp?.data?.cascaded_rollover_class_ids || []).length;
      // Always refetch after a delete so the UI reflects backend truth
      // (defensive — protects against stale optimistic filters and old
      // clients that might have the pre-v235 cascade bug).
      await reloadEntries();
      setDeleteConfirm(null);
      if (deleteConfirm.is_rollover) {
        toast.success('Rollover entry removed. Source entry left untouched.');
      } else if (cascaded > 0) {
        toast.success(`Entry removed + ${cascaded} rollover${cascaded === 1 ? '' : 's'} cleaned up.`);
      } else {
        toast.success('Entry removed');
      }
    } catch {
      toast.error('Failed to remove entry');
    }
  };

  const startEditDraw = (entry) => {
    setDrawEditingId(entry.id);
    setDrawEditValue(entry.draw_number == null ? '' : String(entry.draw_number));
  };

  const cancelEditDraw = () => {
    setDrawEditingId(null);
    setDrawEditValue('');
  };

  const handleSaveDrawNumber = async (entry) => {
    const trimmed = drawEditValue.trim();
    let target = null;
    if (trimmed !== '') {
      const n = parseInt(trimmed, 10);
      if (!Number.isFinite(n) || n <= 0) {
        toast.error('Draw # must be a positive whole number (or blank to clear)');
        return;
      }
      target = n;
    }
    // Skip the call if nothing actually changed.
    if (target === (entry.draw_number ?? null)) {
      cancelEditDraw();
      return;
    }
    setDrawSaving(true);
    try {
      const { data } = await classEntryAPI.setDrawNumber(id, entry.class_id, entry.id, target);
      // Backend may swap with an existing entry holding the same number; reload
      // to surface that side-effect rather than guess locally.
      const newNum = data?.draw_number ?? target;
      setEntries(prev => prev.map(e => e.id === entry.id ? { ...e, draw_number: newNum } : e));
      // Reload to catch any swap of an existing entry.
      loadData();
      toast.success(target == null ? 'Draw # cleared' : `Draw # set to ${target}`);
      cancelEditDraw();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update draw #');
    } finally {
      setDrawSaving(false);
    }
  };

  const handleRolloverEntry = async (entry, targetClassId, targetSidepotId = null) => {
    setRolloverSaving(true);
    try {
      await classEntryAPI.rolloverEntry(id, entry.class_id, entry.id, targetClassId, targetSidepotId);
      const targetName = targetClassId
        ? (show?.classes || []).find(c => c.id === targetClassId)?.name
        : (show?.sidepots || []).find(sp => sp.id === targetSidepotId)?.name;
      toast.success(`Rolled ${entry.rider_name} → ${targetName || 'target'}`);
      setRolloverEntry(null);
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rollover failed');
    } finally {
      setRolloverSaving(false);
    }
  };

  const openAddModal = () => {
    setAddRider('');
    setAddHorse('');
    setAddClassIds(new Set());
    setAddClassOpts({});
    setAddSelectedTargetIds([]);
    setAddTargetCategoryPicks({});
    setAddModal(true);
  };

  // Build a stable synthetic rider_id for producer-added entries so the
  // Target Race opt-in (which is keyed by rider_id) can be matched back to
  // these entries during results computation. Format: `manual:slug-of-name`.
  const synthesizeRiderId = (riderName, horseName) => {
    const slug = (s) => (s || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    const r = slug(riderName);
    const h = slug(horseName);
    if (!r) return '';
    return h ? `manual:${r}--${h}` : `manual:${r}`;
  };

  const toggleAddClass = (cid) => {
    setAddClassIds((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
    // Drop the per-class options when the class is unchecked so we don't
    // ship stale draw#/rollover values on a subsequent re-add.
    setAddClassOpts((prev) => {
      if (!prev[cid]) return prev;
      // If currently in addClassIds, this toggle is REMOVING it → drop opts.
      if (addClassIds.has(cid)) {
        const next = { ...prev };
        delete next[cid];
        return next;
      }
      return prev;
    });
  };

  const setClassOpt = (cid, patch) => {
    setAddClassOpts((prev) => ({
      ...prev,
      [cid]: { ...(prev[cid] || {}), ...patch },
    }));
  };

  const handleAddEntries = async () => {
    if (!addRider.trim()) { toast.error('Rider name is required'); return; }
    if (addClassIds.size === 0) { toast.error('Pick at least one class'); return; }
    // Mirror EntryForm: if a multi-category Target Race is selected the
    // producer MUST pick exactly one category (the times-count-toward
    // single-select rule from v102).
    for (const tid of addSelectedTargetIds) {
      const tgt = targets.find(t => t.id === tid);
      if (!tgt) continue;
      const cats = tgt.categories || [];
      if (cats.length > 1) {
        const picks = addTargetCategoryPicks[tid] || [];
        if (picks.length === 0) {
          toast.error(`Pick a category for ${tgt.name}`);
          return;
        }
      }
    }
    setAddSubmitting(true);
    const manualRiderId = synthesizeRiderId(addRider, addHorse);
    try {
      const classIds = Array.from(addClassIds);
      // Build the actual list of (classId, runIndex) tuples — practice
      // classes with a "runs" count > 1 get cloned that many times so a
      // single click on Add creates N entries for warm-up multi-runs.
      const expanded = [];
      classIds.forEach((cid) => {
        const cls = (show?.classes || []).find((c) => c.id === cid);
        const isPractice = cls && ['warmup', 'exhibition'].includes(cls.class_type);
        const runs = isPractice ? Math.max(1, Math.min(20, parseInt(addClassOpts[cid]?.runs || 1, 10) || 1)) : 1;
        for (let i = 0; i < runs; i++) expanded.push({ cid, runIndex: i });
      });
      const results = await Promise.allSettled(
        expanded.map(({ cid }) => {
          return classEntryAPI.add(id, cid, {
            rider_name: addRider.trim(),
            horse_name: addHorse.trim(),
            is_rollover: false,
            rollover_source_class_id: null,
            // Synthetic id lets Target Race opt-ins link back to these
            // producer-added entries during results computation.
            rider_id: manualRiderId || undefined,
          });
        })
      );
      // Apply draw numbers + fan out to rollover destinations. Draw # only
      // applied to the FIRST entry of each unique class (multi-runs share
      // the same draw # by default).
      const drawCalls = [];
      const rolloverCalls = [];
      let rolloverAdded = 0;
      const seenClass = new Set();
      results.forEach((r, i) => {
        if (r.status !== 'fulfilled') return;
        const { cid, runIndex } = expanded[i];
        const entryId = r.value?.data?.id;
        const opts = addClassOpts[cid] || {};
        if (runIndex === 0) {
          const drawRaw = (opts.drawNumber || '').trim();
          if (drawRaw) {
            const drawNum = parseInt(drawRaw, 10);
            if (Number.isFinite(drawNum) && drawNum > 0 && entryId) {
              drawCalls.push(classEntryAPI.setDrawNumber(id, cid, entryId, drawNum));
            }
          }
          const destIds = opts.rolloverDestClassIds || [];
          destIds.forEach((destId) => {
            if (!destId || !entryId || seenClass.has(`${cid}->${destId}`)) return;
            seenClass.add(`${cid}->${destId}`);
            const isSp = (show?.sidepots || []).some((sp) => sp.id === destId);
            rolloverCalls.push(
              (isSp
                ? classEntryAPI.rolloverEntry(id, cid, entryId, null, destId)
                : classEntryAPI.rolloverEntry(id, cid, entryId, destId)
              ).then(() => { rolloverAdded += 1; })
            );
          });
        }
      });
      if (drawCalls.length) await Promise.allSettled(drawCalls);
      if (rolloverCalls.length) await Promise.allSettled(rolloverCalls);
      // Target Race opt-ins — best-effort. Producer opts the rider into each
      // selected target race using the same synthetic rider_id we passed on
      // the entry add, so results computation can match these manual entries
      // to the opt-in record. Multi-category targets need a category pick;
      // single-category targets default to that one category.
      let targetsAdded = 0;
      if (addSelectedTargetIds.length > 0 && manualRiderId) {
        const targetCalls = addSelectedTargetIds.map((tid) => {
          const tgt = targets.find((t) => t.id === tid);
          const cats = tgt?.categories || [];
          const eligibleCats = cats.filter((c) =>
            (c.class_ids || []).some((cid) => addClassIds.has(cid))
          );
          const defaultCat = (eligibleCats[0] || cats[0])?.id;
          const picks = addTargetCategoryPicks[tid] || (defaultCat ? [defaultCat] : []);
          return api.post(`/shows/${id}/targets/${tid}/opt-in`, {
            rider_id: manualRiderId,
            rider_name: addRider.trim(),
            horse_name: addHorse.trim(),
            selected_category_ids: picks.length ? picks : undefined,
          }).then(() => { targetsAdded += 1; });
        });
        await Promise.allSettled(targetCalls);
      }
      const added = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - added;
      const practiceRuns = expanded.filter((x) => x.runIndex > 0).length;
      if (added > 0) {
        const base = added === 1 ? 'Entry added' : `Added ${addRider.trim()} — ${added} entries created`;
        const extras = [];
        if (practiceRuns > 0) extras.push(`${practiceRuns} extra practice run${practiceRuns !== 1 ? 's' : ''}`);
        if (rolloverAdded > 0) extras.push(`${rolloverAdded} rollover${rolloverAdded !== 1 ? 's' : ''} copied`);
        if (targetsAdded > 0) extras.push(`${targetsAdded} target opt-in${targetsAdded !== 1 ? 's' : ''}`);
        toast.success(extras.length ? `${base} · ${extras.join(' · ')}` : base);
      }
      if (failed > 0) {
        const firstErr = results.find((r) => r.status === 'rejected');
        toast.error(firstErr?.reason?.response?.data?.detail || `${failed} class add(s) failed`);
      }
      setAddModal(false);
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add entries');
    } finally {
      setAddSubmitting(false);
    }
  };

  const [importClassId, setImportClassId] = useState(null);
  const [importFile, setImportFile] = useState(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Show class picker modal
    setImportFile(file);
    setImportClassId('');
    e.target.value = '';
  };

  const handleImportConfirm = async () => {
    if (!importFile || !importClassId) return;
    const ext = importFile.name.split('.').pop().toLowerCase();
    const isAI = ['pdf', 'jpg', 'jpeg', 'png', 'docx', 'doc'].includes(ext);
    setImportLoading(true);
    if (isAI) toast.info('AI is reading your document... This may take a moment');
    try {
      const { data } = await classEntryAPI.bulkImport(id, importClassId, importFile);
      let msg = `${data.added} entr${data.added !== 1 ? 'ies' : 'y'} imported`;
      if (data.scores_added > 0) msg += ` with ${data.scores_added} times`;
      toast.success(msg);
      if (data.errors?.length > 0) toast.error(`${data.errors.length} row${data.errors.length !== 1 ? 's' : ''} had errors`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImportLoading(false);
      setImportFile(null);
      setImportClassId(null);
    }
  };

  const loadRefundRequests = async () => {
    try {
      const { data } = await refundAPI.listForShow(id);
      setRefundRequests(data || []);
    } catch { /* ignore */ }
  };

  const handleApproveRefund = async (requestId) => {
    setProcessingRefund(requestId);
    try {
      await refundAPI.approve(requestId);
      toast.success('Refund approved and processed via Stripe');
      loadRefundRequests();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process refund');
    } finally {
      setProcessingRefund(null);
    }
  };

  const handleDenyRefund = async () => {
    if (!denyModal) return;
    setProcessingRefund(denyModal);
    try {
      await refundAPI.deny(denyModal, denyReason);
      toast.success('Refund request denied');
      setDenyModal(null);
      setDenyReason('');
      loadRefundRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deny refund');
    } finally {
      setProcessingRefund(null);
    }
  };

  const handleDirectRefund = async () => {
    if (!directRefundConfirm) return;
    setProcessingRefund(directRefundConfirm.riderId);
    try {
      const { data } = await refundAPI.direct(id, directRefundConfirm.riderId);
      toast.success(`Refund of $${data.refund_amount?.toFixed(2) || '0.00'} processed for ${directRefundConfirm.riderName}`);
      setDirectRefundConfirm(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process refund');
    } finally {
      setProcessingRefund(null);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><div className="shimmer h-12 w-12 rounded-full"></div></div>;
  if (!show) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><p className="text-white">Show not found</p></div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Nav */}
      <nav className="bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <button onClick={() => navigate(`/shows/${id}`)} className="flex items-center gap-2 text-[#9E9E9E] hover:text-white transition-colors text-sm" data-testid="back-to-show-btn">
              <ArrowLeft size={18} /> <span className="hidden sm:inline">Back to Show</span><span className="sm:hidden">Back</span>
            </button>
            <h2 className="text-white font-bold text-sm sm:text-base truncate max-w-[200px] sm:max-w-none" style={{ fontFamily: 'Outfit' }}>Manage Entries</h2>
            <div className="w-16" />
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-white mb-1" style={{ fontFamily: 'Outfit' }} data-testid="manage-entries-title">{show.name}</h1>
            <p className="text-[#9E9E9E] text-sm">Manage all entries across {show.classes.length} classes</p>
          </div>
          <div className="flex items-center gap-2 self-start">
            <button
              onClick={openReport}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#FF9500]/10 border border-[#FF9500]/30 text-[#FF9500] text-xs sm:text-sm font-bold rounded-lg hover:bg-[#FF9500]/20 transition-colors"
              data-testid="show-revenue-report-btn"
            >
              <ChartBar size={16} weight="bold" /> Show Report
            </button>
            <button
              onClick={openBalanceOwed}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] text-xs sm:text-sm font-bold rounded-lg hover:bg-[#FF3B30]/20 transition-colors"
              data-testid="balance-owed-btn"
            >
              <ChartBar size={16} weight="bold" /> Balance Owed
            </button>
            <button
              onClick={openAddModal}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#0096FF] text-white text-xs sm:text-sm font-bold rounded-lg hover:bg-[#0096FF]/80 transition-colors"
              data-testid="manage-add-entry-btn"
            >
              <Plus size={16} weight="bold" /> Add Entry
            </button>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={importLoading}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#34C759]/10 border border-[#34C759]/30 text-[#34C759] text-xs sm:text-sm font-bold rounded-lg hover:bg-[#34C759]/20 transition-colors disabled:opacity-50"
              data-testid="import-entries-btn"
            >
              <UploadSimple size={16} weight="bold" /> {importLoading ? 'Importing...' : 'Import Entries'}
            </button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.pdf,.jpg,.jpeg,.png,.docx,.doc" onChange={handleFileUpload} className="hidden" data-testid="entry-file-input" />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-[#161616] border border-[#262626] rounded-lg p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs text-[#9E9E9E] mb-1">Total Entries</p>
            <p className="text-xl sm:text-2xl font-bold text-white" data-testid="stat-total">{stats.total}</p>
          </div>
          <div className="bg-[#161616] border border-[#262626] rounded-lg p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs text-[#34C759] mb-1">Paid</p>
            <p className="text-xl sm:text-2xl font-bold text-[#34C759]" data-testid="stat-paid">{stats.paid}</p>
          </div>
          <div className="bg-[#161616] border border-[#FF3B30]/30 rounded-lg p-3 sm:p-4 cursor-pointer hover:border-[#FF3B30]/60 transition-colors" onClick={() => setPaymentFilter(paymentFilter === 'UNPAID' ? 'ALL' : 'UNPAID')}>
            <p className="text-[10px] sm:text-xs text-[#FF3B30] mb-1">Unpaid {stats.unpaidFees > 0 ? `($${stats.unpaidFees.toFixed(0)})` : ''}</p>
            <p className="text-xl sm:text-2xl font-bold text-[#FF3B30]" data-testid="stat-unpaid">{stats.unpaid}</p>
          </div>
          <div className="bg-[#161616] border border-[#262626] rounded-lg p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs text-[#0096FF] mb-1">Comp</p>
            <p className="text-xl sm:text-2xl font-bold text-[#0096FF]" data-testid="stat-comp">{stats.comp}</p>
          </div>
        </div>

        {/* Top-level tabs: Entries vs Stalls/RV vs Notes */}
        <div className="flex items-center gap-1 mb-6 border-b border-[#262626]" data-testid="manage-entries-tabs">
          <button
            onClick={() => setActivePageTab('ENTRIES')}
            className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors -mb-px ${activePageTab === 'ENTRIES' ? 'border-[#FF9500] text-white' : 'border-transparent text-[#9E9E9E] hover:text-white'}`}
            data-testid="tab-entries"
          >
            Entries
          </button>
          <button
            onClick={() => setActivePageTab('STALLS')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-bold border-b-2 transition-colors -mb-px ${activePageTab === 'STALLS' ? 'border-[#FF9500] text-white' : 'border-transparent text-[#9E9E9E] hover:text-white'}`}
            data-testid="tab-stalls"
          >
            <Bed size={14} /> Stalls / RV
          </button>
          <button
            onClick={() => setActivePageTab('STAFF')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-bold border-b-2 transition-colors -mb-px ${activePageTab === 'STAFF' ? 'border-[#FF9500] text-white' : 'border-transparent text-[#9E9E9E] hover:text-white'}`}
            data-testid="tab-staff"
          >
            <Users size={14} /> Staff Charges
          </button>
          <button
            onClick={() => setActivePageTab('ADDONS')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-bold border-b-2 transition-colors -mb-px ${activePageTab === 'ADDONS' ? 'border-[#FF9500] text-white' : 'border-transparent text-[#9E9E9E] hover:text-white'}`}
            data-testid="tab-addons"
          >
            <Package size={14} /> Add-Ons
          </button>
          <button
            onClick={() => setActivePageTab('TARGETS')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-bold border-b-2 transition-colors -mb-px ${activePageTab === 'TARGETS' ? 'border-[#FF9500] text-white' : 'border-transparent text-[#9E9E9E] hover:text-white'}`}
            data-testid="tab-targets"
          >
            <Crosshair size={14} /> Targets
          </button>
          <button
            onClick={() => setActivePageTab('RIDER_CHARGES')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-bold border-b-2 transition-colors -mb-px ${activePageTab === 'RIDER_CHARGES' ? 'border-[#FF9500] text-white' : 'border-transparent text-[#9E9E9E] hover:text-white'}`}
            data-testid="tab-rider-charges"
          >
            <ListBullets size={14} /> Rider Charges
          </button>
          <button
            onClick={() => setActivePageTab('NOTES')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-bold border-b-2 transition-colors -mb-px ${activePageTab === 'NOTES' ? 'border-[#FF9500] text-white' : 'border-transparent text-[#9E9E9E] hover:text-white'}`}
            data-testid="tab-notes"
          >
            <Note size={14} /> Notes
          </button>
        </div>

        {activePageTab === 'NOTES' && (
          <OfficeNotesTab showId={id} entries={entries} />
        )}

        {activePageTab === 'STALLS' && (
          <StallsTab showId={id} showName={show?.name} />
        )}

        {activePageTab === 'STAFF' && (
          <StaffInvoicesSection showId={id} showName={show?.name} />
        )}

        {activePageTab === 'ADDONS' && (
          <AddonsTab showId={id} />
        )}

        {activePageTab === 'TARGETS' && (
          <TargetsTab showId={id} show={show} />
        )}

        {activePageTab === 'RIDER_CHARGES' && (
          <RiderChargesTab showId={id} />
        )}

        <div style={{ display: activePageTab === 'ENTRIES' ? 'block' : 'none' }}>

        {/* Refund Requests */}
        {refundRequests.length > 0 && (
          <div className="mb-6 bg-[#161616] border border-[#FF9500]/20 rounded-lg p-4" data-testid="refund-requests-panel">
            <h3 className="text-sm font-bold text-[#FF9500] mb-3 flex items-center gap-2">
              <ArrowCounterClockwise size={16} weight="bold" /> Refund Requests ({refundRequests.filter(r => r.status === 'PENDING').length} pending)
            </h3>
            <div className="space-y-2">
              {refundRequests.map(req => (
                <div key={req.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-[#0A0A0A]/50 border border-[#262626]" data-testid={`refund-request-${req.id}`}>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-semibold">{req.rider_name}</p>
                    <p className="text-[#9E9E9E] text-xs">${req.refund_amount?.toFixed(2) || '0.00'} &bull; {new Date(req.created_at).toLocaleDateString()}</p>
                    {req.reason && <p className="text-[#555] text-xs mt-0.5 italic">"{req.reason}"</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {req.status === 'PENDING' ? (
                      <>
                        <button
                          onClick={() => handleApproveRefund(req.id)}
                          disabled={processingRefund === req.id}
                          className="px-3 py-1.5 text-xs font-bold bg-[#34C759]/15 text-[#34C759] rounded-lg hover:bg-[#34C759]/25 transition-colors disabled:opacity-50"
                          data-testid={`approve-refund-${req.id}`}
                        >
                          {processingRefund === req.id ? 'Processing...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => { setDenyModal(req.id); setDenyReason(''); }}
                          disabled={processingRefund === req.id}
                          className="px-3 py-1.5 text-xs font-bold bg-[#FF3B30]/15 text-[#FF3B30] rounded-lg hover:bg-[#FF3B30]/25 transition-colors disabled:opacity-50"
                          data-testid={`deny-refund-${req.id}`}
                        >
                          Deny
                        </button>
                      </>
                    ) : (
                      <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${
                        req.status === 'APPROVED' ? 'bg-[#34C759]/15 text-[#34C759]' : 'bg-[#FF3B30]/15 text-[#FF3B30]'
                      }`}>{req.status}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* View mode: Table (rider/class) vs Run Order (draw # flat list).
            Run Order is built specifically for the announcer/timer at the
            gate — flat list across classes sorted by draw # with the next-up
            rider highlighted. The toggle sits above the day tabs so producers
            can flip into the live-show view in one tap. */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="inline-flex bg-[#161616] border border-[#262626] rounded-lg p-1" data-testid="view-mode-toggle">
            <button
              onClick={() => setViewMode('TABLE')}
              data-testid="view-mode-table-btn"
              className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${
                viewMode === 'TABLE' ? 'bg-[#0096FF] text-white' : 'text-[#9E9E9E] hover:text-white'
              }`}
            >
              Manage
            </button>
            <button
              onClick={() => setViewMode('RUN_ORDER')}
              data-testid="view-mode-runorder-btn"
              className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${
                viewMode === 'RUN_ORDER' ? 'bg-[#0096FF] text-white' : 'text-[#9E9E9E] hover:text-white'
              }`}
            >
              Run Order
            </button>
          </div>
          {viewMode === 'RUN_ORDER' && (
            <p className="text-[10px] text-[#5E5E5E] uppercase tracking-wider" data-testid="run-order-hint">
              Tap a row to enter time · Enter to save & advance
            </p>
          )}
        </div>

        {/* Day Tabs */}
        {dayKeys.length > 0 && (
          <div className="flex gap-1 mb-5 bg-[#161616] rounded-lg p-1 overflow-x-auto" data-testid="manage-day-tabs">
            <button
              onClick={() => setActiveDayTab('ALL')}
              data-testid="manage-day-tab-all"
              className={`flex-shrink-0 px-4 py-2.5 rounded-md text-sm font-bold transition-all ${
                activeDayTab === 'ALL'
                  ? 'bg-[#0096FF] text-white'
                  : 'text-[#9E9E9E] hover:text-white hover:bg-[#262626]'
              }`}
            >
              All Days
              <span className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black ${
                activeDayTab === 'ALL' ? 'bg-white/25 text-white' : 'bg-[#262626] text-[#9E9E9E]'
              }`}>{entries.length}</span>
            </button>
            {dayKeys.map((day) => {
              const dayEntryCount = entries.filter(e => (classIdsByDay[day] || []).includes(e.class_id)).length;
              // Date keys (YYYY-MM-DD) → "Friday Jun 5". Plain weekday keys
              // ("Friday") render as-is.
              let label = day;
              if (/^\d{4}-\d{2}-\d{2}$/.test(day)) {
                const dt = new Date(day + 'T12:00:00');
                label = dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
              }
              return (
                <button
                  key={day}
                  onClick={() => setActiveDayTab(day)}
                  data-testid={`manage-day-tab-${day}`}
                  className={`flex-shrink-0 px-4 py-2.5 rounded-md text-sm font-bold transition-all ${
                    activeDayTab === day
                      ? 'bg-[#0096FF] text-white'
                      : 'text-[#9E9E9E] hover:text-white hover:bg-[#262626]'
                  }`}
                >
                  {label}
                  <span className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black ${
                    activeDayTab === day ? 'bg-white/25 text-white' : 'bg-[#262626] text-[#9E9E9E]'
                  }`}>{dayEntryCount}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Search & Filters */}
        <div className="mb-4 space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9E9E9E]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by rider or horse name..."
                className="w-full pl-9 pr-4 py-2.5 bg-[#161616] border border-[#262626] rounded-lg text-white text-sm focus:outline-none focus:border-[#0096FF] transition-colors placeholder:text-[#555]"
                data-testid="entry-search-input"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${showFilters ? 'bg-[#0096FF]/15 text-[#0096FF] border border-[#0096FF]/30' : 'bg-[#161616] text-[#9E9E9E] border border-[#262626] hover:border-[#0096FF]/30'}`}
              data-testid="toggle-filters-btn"
            >
              <Funnel size={16} weight="bold" /> Filters
            </button>
          </div>

          {showFilters && (
            <div className="flex flex-col sm:flex-row gap-3 p-4 bg-[#161616] border border-[#262626] rounded-lg" data-testid="filters-panel">
              <div className="flex-1">
                <label className="block text-xs text-[#9E9E9E] mb-1.5">Class</label>
                <div className="relative">
                  <select
                    value={classFilter}
                    onChange={(e) => setClassFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF] appearance-none cursor-pointer"
                    data-testid="class-filter-select"
                  >
                    <option value="ALL">All Classes</option>
                    {classNames.map(cn => <option key={cn} value={cn}>{cn}</option>)}
                  </select>
                  <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9E9E9E] pointer-events-none" />
                </div>
              </div>
              <div className="flex-1">
                <label className="block text-xs text-[#9E9E9E] mb-1.5">Payment Status</label>
                <div className="relative">
                  <select
                    value={paymentFilter}
                    onChange={(e) => setPaymentFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF] appearance-none cursor-pointer"
                    data-testid="payment-filter-select"
                  >
                    <option value="ALL">All Statuses</option>
                    <option value="PAID">Paid</option>
                    <option value="PENDING">Pending</option>
                    <option value="UNPAID">Unpaid</option>
                    <option value="COMP">Comp</option>
                    <option value="REFUNDED">Refunded</option>
                  </select>
                  <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9E9E9E] pointer-events-none" />
                </div>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => { setClassFilter('ALL'); setPaymentFilter('ALL'); setSearch(''); setActiveDayTab('ALL'); }}
                  className="px-4 py-2 text-xs text-[#9E9E9E] hover:text-white transition-colors"
                  data-testid="clear-filters-btn"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results count */}
        {/* Results count */}
        <p className="text-[#9E9E9E] text-xs mb-3" data-testid="results-count">
          {viewMode === 'RUN_ORDER'
            ? `${runOrderRows.length} riders in run order`
            : `Showing ${filteredEntries.length} of ${entries.length} entries`}
        </p>

        {viewMode === 'RUN_ORDER' ? (
          <div className="bg-[#161616] border border-[#262626] rounded-lg overflow-hidden" data-testid="run-order-panel">
            {runOrderRows.length === 0 ? (
              <div className="p-12 text-center" data-testid="run-order-empty">
                <Horse size={48} className="text-[#262626] mx-auto mb-3" />
                <p className="text-[#9E9E9E] text-sm">
                  No riders in this day&apos;s run order yet.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead>
                    <tr className="border-b border-[#262626] bg-[#0A0A0A]/50">
                      <th className="text-center text-xs text-[#9E9E9E] font-semibold p-3 pl-4 w-20">#</th>
                      <th className="text-left text-xs text-[#9E9E9E] font-semibold p-3">Rider · Horse</th>
                      <th className="text-left text-xs text-[#9E9E9E] font-semibold p-3">Class</th>
                      <th className="text-right text-xs text-[#9E9E9E] font-semibold p-3 pr-4">Time / Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Insert a class-section header row whenever the class
                      // changes so the announcer can see "Open Barrels" / "Prime
                      // Barrels" boundaries at a glance.
                      let lastClassId = null;
                      const out = [];
                      for (const e of runOrderRows) {
                        if (e.class_id !== lastClassId) {
                          lastClassId = e.class_id;
                          out.push(
                            <tr key={`hdr-${e.class_id}`} className="bg-[#0A0A0A]" data-testid={`run-order-section-${e.class_id}`}>
                              <td colSpan={4} className="px-4 py-2 text-[10px] uppercase tracking-wider text-[#9E9E9E] font-bold">
                                {e.class_name}
                                {e.scheduled_day && (
                                  <span className="text-[#5E5E5E] font-normal ml-2">· {e.scheduled_day}</span>
                                )}
                              </td>
                            </tr>
                          );
                        }
                        const isNext = e.id === nextUpId;
                        const isTiming = e.id === timingEntryId;
                        const isCompleted = e.score && e.score.status === 'COMPLETED' && e.score.final_time != null;
                        const s = e.score;
                        let statusEl;
                        if (s && s.status === 'COMPLETED' && s.final_time != null) {
                          statusEl = <span className="text-[#34C759] font-bold tabular-nums">{s.final_time.toFixed(3)}s</span>;
                        } else if (s && s.status === 'NT') {
                          statusEl = <span className="text-[#FF9500] font-bold">NT</span>;
                        } else if (s && s.status === 'SCR') {
                          statusEl = <span className="text-[#FF3B30] font-bold">SCR</span>;
                        } else if (e.payment_status !== 'PAID' && e.payment_status !== 'COMP') {
                          statusEl = <span className="text-[#FACC15] text-[10px] font-bold uppercase">{e.payment_status || 'UNPAID'}</span>;
                        } else {
                          statusEl = <span className="text-[#5E5E5E] text-xs">—</span>;
                        }
                        out.push(
                          <tr
                            key={e.id}
                            data-testid={`run-order-row-${e.id}`}
                            onClick={() => {
                              // Click opens inline timing for any row that
                              // doesn't already have a final time. Re-clicking
                              // a completed row re-opens to allow fixes.
                              if (isTiming) return;
                              openTimingFor(e.id);
                            }}
                            className={`border-b border-[#1a1a1a] transition-colors cursor-pointer ${
                              isTiming
                                ? 'bg-[#0096FF]/15 ring-1 ring-[#0096FF]'
                                : isNext
                                  ? 'bg-[#0096FF]/10 hover:bg-[#0096FF]/15'
                                  : 'hover:bg-[#0A0A0A]/50'
                            }`}
                          >
                            <td className="p-3 pl-4 text-center">
                              {e.draw_number ? (
                                <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full text-sm font-black ${
                                  isNext || isTiming ? 'bg-[#0096FF] text-white' : 'bg-[#0096FF]/10 text-[#0096FF]'
                                }`}>{e.draw_number}</span>
                              ) : (
                                <span className="text-[#5E5E5E] text-[10px] uppercase">no #</span>
                              )}
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-semibold ${isNext || isTiming ? 'text-white' : 'text-white/90'}`}>{e.rider_name}</span>
                                {e.horse_name && <span className="text-[#9E9E9E] text-xs">on {e.horse_name}</span>}
                                {e.is_rollover && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#FF9500]/15 text-[#FF9500] rounded text-[9px] font-bold">
                                    RO{rolloverSourceName(e) ? <span className="font-semibold opacity-90 normal-case">&middot; from {rolloverSourceName(e)}</span> : null}
                                  </span>
                                )}
                                {isNext && !isTiming && (
                                  <span className="px-1.5 py-0.5 bg-[#0096FF] text-white rounded text-[9px] font-bold uppercase tracking-wide">Next up</span>
                                )}
                                {isCompleted && !isTiming && (
                                  <span className="text-[#5E5E5E] text-[10px] uppercase tracking-wider ml-1">tap to fix</span>
                                )}
                              </div>
                            </td>
                            <td className="p-3">
                              <span className="text-[#9E9E9E] text-xs truncate max-w-[180px] block">{e.class_name}</span>
                            </td>
                            <td className="p-3 pr-4 text-right" onClick={(ev) => isTiming && ev.stopPropagation()}>
                              {isTiming ? (
                                <div className="inline-flex items-center gap-1.5 flex-wrap justify-end" data-testid={`run-order-timer-${e.id}`}>
                                  <input
                                    ref={timingInputRef}
                                    type="number"
                                    step="0.001"
                                    inputMode="decimal"
                                    value={timingValue}
                                    onChange={(ev) => setTimingValue(ev.target.value)}
                                    onKeyDown={(ev) => {
                                      if (ev.key === 'Enter') { ev.preventDefault(); submitTime(); }
                                      if (ev.key === 'Escape') { ev.preventDefault(); setTimingEntryId(null); setTimingValue(''); }
                                    }}
                                    placeholder="17.234"
                                    disabled={timingSaving}
                                    className="w-24 px-2 py-1 bg-[#0A0A0A] border border-[#0096FF]/40 rounded text-white text-sm font-bold tabular-nums focus:outline-none focus:border-[#0096FF]"
                                    data-testid={`run-order-time-input-${e.id}`}
                                  />
                                  <select
                                    value={timingKnocks}
                                    onChange={(ev) => setTimingKnocks(Number(ev.target.value))}
                                    disabled={timingSaving}
                                    className="px-1.5 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs"
                                    title="Knocks (5s each)"
                                    data-testid={`run-order-knocks-${e.id}`}
                                  >
                                    <option value={0}>0K</option>
                                    <option value={1}>1K</option>
                                    <option value={2}>2K</option>
                                    <option value={3}>3K</option>
                                  </select>
                                  <button
                                    onClick={() => submitTime('NT')}
                                    disabled={timingSaving}
                                    className="px-2 py-1 bg-[#FF9500]/15 text-[#FF9500] border border-[#FF9500]/30 rounded text-[10px] font-bold hover:bg-[#FF9500]/25 disabled:opacity-50"
                                    data-testid={`run-order-nt-${e.id}`}
                                    title="No time"
                                  >NT</button>
                                  <button
                                    onClick={() => submitTime('SCR')}
                                    disabled={timingSaving}
                                    className="px-2 py-1 bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/30 rounded text-[10px] font-bold hover:bg-[#FF3B30]/25 disabled:opacity-50"
                                    data-testid={`run-order-scr-${e.id}`}
                                    title="Scratch"
                                  >SCR</button>
                                  <button
                                    onClick={() => { setTimingEntryId(null); setTimingValue(''); }}
                                    disabled={timingSaving}
                                    className="p-1 text-[#9E9E9E] hover:text-white"
                                    data-testid={`run-order-cancel-${e.id}`}
                                    title="Cancel"
                                  ><X size={14} /></button>
                                </div>
                              ) : (
                                <div className="flex items-center justify-end gap-1.5">
                                  {statusEl}
                                  {!isCompleted && (
                                    <ClockCounterClockwise size={12} className="text-[#0096FF]/60" />
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      }
                      return out;
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
        /* Entries Table (default Manage view) */
        <div className="bg-[#161616] border border-[#262626] rounded-lg overflow-hidden">
          {filteredEntries.length === 0 ? (
            <div className="p-12 text-center" data-testid="no-entries-found">
              <Horse size={48} className="text-[#262626] mx-auto mb-3" />
              <p className="text-[#9E9E9E] text-sm">{entries.length === 0 ? 'No entries yet for this show' : 'No entries match your filters'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-[#262626] bg-[#0A0A0A]/50">
                    <th className="text-left text-xs text-[#9E9E9E] font-semibold p-3 pl-4">Rider</th>
                    <th className="text-left text-xs text-[#9E9E9E] font-semibold p-3">Horse</th>
                    <th className="text-left text-xs text-[#9E9E9E] font-semibold p-3">Class</th>
                    <th className="text-center text-xs text-[#9E9E9E] font-semibold p-3">Payment</th>
                    <th className="text-center text-xs text-[#9E9E9E] font-semibold p-3">Draw #</th>
                    <th className="text-right text-xs text-[#9E9E9E] font-semibold p-3 pr-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map((entry) => (
                    <React.Fragment key={entry.id}>
                    <tr className="border-b border-[#1a1a1a] hover:bg-[#0A0A0A]/50 transition-colors" data-testid={`manage-entry-${entry.id}`}>
                      <td className="p-3 pl-4">
                        <div className="flex items-center gap-2">
                          <User size={14} className="text-[#555] flex-shrink-0" />
                          <span className="text-white text-sm font-semibold truncate max-w-[150px]">{entry.rider_name}</span>
                          {entry.is_rollover && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#FF9500]/15 text-[#FF9500] rounded text-[9px] font-bold flex-shrink-0" data-testid={`rollover-source-label-${entry.id}`}>
                              RO{rolloverSourceName(entry) ? <span className="font-semibold opacity-90 normal-case">&middot; from {rolloverSourceName(entry)}</span> : null}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-[#9E9E9E] text-sm truncate max-w-[120px]">{entry.horse_name || '-'}</td>
                      <td className="p-3">
                        <span className="text-white text-sm truncate max-w-[150px] block">{entry.class_name}</span>
                      </td>
                      <td className="p-3 text-center">
                        <div className="inline-flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => handleTogglePayment(entry)}
                            data-testid={`payment-toggle-${entry.id}`}
                            className={`px-2 py-1 rounded text-[10px] font-bold uppercase cursor-pointer transition-colors ${
                              entry.payment_status === 'PAID' ? 'bg-[#34C759]/15 text-[#34C759] hover:bg-[#34C759]/25' :
                              entry.payment_status === 'COMP' ? 'bg-[#0096FF]/15 text-[#0096FF] hover:bg-[#0096FF]/25' :
                              entry.payment_status === 'PENDING' ? 'bg-[#FACC15]/15 text-[#FACC15] hover:bg-[#FACC15]/25' :
                              entry.payment_status === 'REFUNDED' ? 'bg-[#AF52DE]/15 text-[#AF52DE]' :
                              'bg-[#FF3B30]/15 text-[#FF3B30] hover:bg-[#FF3B30]/25'
                            }`}
                            title="Click to toggle payment status"
                          >
                            {entry.payment_status || 'UNPAID'}
                          </button>
                          {entry.awaiting_check && entry.payment_status !== 'PAID' && (() => {
                            const due = entry.check_due_date ? new Date(entry.check_due_date) : null;
                            const overdue = due && due < new Date();
                            const dueLabel = due ? due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
                            return (
                              <span
                                className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${overdue ? 'bg-[#FF3B30]/15 text-[#FF3B30]' : 'bg-[#FF9500]/15 text-[#FF9500]'}`}
                                title={overdue ? `Mail-in check was due ${dueLabel} — overdue` : `Mail-in check due ${dueLabel}`}
                                data-testid={`manage-check-badge-${entry.id}`}
                              >
                                {overdue ? `CHECK OVERDUE` : `CHECK · ${dueLabel}`}
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        {drawEditingId === entry.id ? (
                          <div className="flex items-center justify-center gap-1">
                            <input
                              type="number"
                              min={1}
                              autoFocus
                              value={drawEditValue}
                              onChange={(e) => setDrawEditValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveDrawNumber(entry);
                                else if (e.key === 'Escape') cancelEditDraw();
                              }}
                              onBlur={() => handleSaveDrawNumber(entry)}
                              disabled={drawSaving}
                              className="w-14 px-1.5 py-1 bg-[#0A0A0A] border border-[#0096FF] rounded text-white text-xs text-center focus:outline-none"
                              placeholder="–"
                              data-testid={`draw-input-${entry.id}`}
                            />
                          </div>
                        ) : (
                          <button
                            onClick={() => startEditDraw(entry)}
                            title="Click to set or change draw #"
                            data-testid={`draw-edit-${entry.id}`}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-colors hover:ring-2 hover:ring-[#0096FF]/40"
                          >
                            {entry.draw_number ? (
                              <span className="w-7 h-7 inline-flex items-center justify-center bg-[#0096FF]/15 text-[#0096FF] rounded-full">{entry.draw_number}</span>
                            ) : (
                              <span className="w-7 h-7 inline-flex items-center justify-center text-[#555] hover:text-[#0096FF]">–</span>
                            )}
                          </button>
                        )}
                      </td>
                      <td className="p-3 pr-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {((show?.classes || []).filter(c => c.id !== entry.class_id).length > 0 || (show?.sidepots || []).length > 0) && (
                            <button
                              onClick={() => setRolloverEntry(rolloverEntry?.id === entry.id ? null : entry)}
                              className={`p-1.5 rounded transition-colors ${rolloverEntry?.id === entry.id ? 'bg-[#FF9500]/20' : 'hover:bg-[#FF9500]/10'}`}
                              title="Rollover to another class or sidepot"
                              data-testid={`rollover-btn-${entry.id}`}
                            >
                              <ClockCounterClockwise size={14} className="text-[#FF9500]" />
                            </button>
                          )}
                          {show?.tie_out_fee_enabled && entry.payment_status !== 'PAID' && entry.payment_status !== 'COMP' && (
                            <button
                              onClick={() => handleToggleTieOutWaive(entry)}
                              className={`p-1.5 rounded transition-colors ${entry.tie_out_waived ? 'bg-[#FF9500]/20' : 'hover:bg-[#FF9500]/10'}`}
                              title={entry.tie_out_waived ? 'Tie-out fee WAIVED — click to re-bill' : 'Waive tie-out fee (rider already has a stall)'}
                              data-testid={`tie-out-waive-${entry.id}`}
                            >
                              <Tent size={14} className={entry.tie_out_waived ? 'text-[#FF9500]' : 'text-[#9E9E9E]'} weight={entry.tie_out_waived ? 'fill' : 'regular'} />
                            </button>
                          )}
                          {entry.payment_status === 'PAID' && entry.rider_id && (
                            <button
                              onClick={() => setDirectRefundConfirm({ riderId: entry.rider_id, riderName: entry.rider_name })}
                              className="p-1.5 hover:bg-[#FF9500]/10 rounded transition-colors"
                              title="Issue refund"
                              data-testid={`direct-refund-${entry.id}`}
                            >
                              <ArrowCounterClockwise size={14} className="text-[#FF9500]" />
                            </button>
                          )}
                          <button
                            onClick={() => handleStartEdit(entry)}
                            className="p-1.5 hover:bg-[#262626] rounded transition-colors"
                            title="Edit entry"
                            data-testid={`edit-manage-entry-${entry.id}`}
                          >
                            <PencilSimple size={14} className="text-[#9E9E9E]" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(entry)}
                            className="p-1.5 hover:bg-[#FF3B30]/10 rounded transition-colors"
                            title="Delete entry"
                            data-testid={`delete-manage-entry-${entry.id}`}
                          >
                            <Trash size={14} className="text-[#FF3B30]" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {/* Rollover target picker — opens below the entry row */}
                    {rolloverEntry?.id === entry.id && (() => {
                      // v264 — Same-day filter. Rider entries can't roll
                      // into classes/sidepots scheduled on a different
                      // day (matches backend enforcement + rider self-
                      // entry map). Fallback: if either side lacks a
                      // scheduled day, allow it (legacy single-day).
                      const src = (show?.classes || []).find(c => c.id === entry.class_id) || {};
                      const rolloverClasses = (show?.classes || []).filter(c => c.id !== entry.class_id && isSameDay(src, c));
                      const rolloverSidepots = (show?.sidepots || []).filter(sp => isSameDay(src, sp));
                      return (
                      <tr className="bg-[#0A0A0A] border-b border-[#FF9500]/30" data-testid={`rollover-picker-${entry.id}`}>
                        <td colSpan={6} className="p-3 pl-4 pr-4">
                          <div className="flex items-start gap-3 flex-wrap">
                            <div className="flex items-center gap-1.5 text-xs text-[#FF9500] font-semibold">
                              <ClockCounterClockwise size={12} weight="bold" />
                              Roll {entry.rider_name} into:
                            </div>
                            <div className="flex flex-wrap gap-1.5 flex-1">
                              {rolloverClasses.length === 0 && rolloverSidepots.length === 0 && (
                                <span className="text-[11px] text-[#9E9E9E] italic">No same-day rollover targets available</span>
                              )}
                              {rolloverClasses.map(cls => (
                                  <button
                                    key={cls.id}
                                    onClick={() => handleRolloverEntry(entry, cls.id, null)}
                                    disabled={rolloverSaving}
                                    className="px-2 py-1 bg-[#161616] border border-[#262626] rounded text-[11px] text-white hover:border-[#FF9500] hover:bg-[#FF9500]/10 transition-colors disabled:opacity-40"
                                    data-testid={`rollover-to-class-${cls.id}-${entry.id}`}
                                  >
                                    {cls.name}
                                    {cls.scheduled_day && <span className="ml-1 text-[#0096FF]">({cls.scheduled_day})</span>}
                                  </button>
                                ))}
                              {rolloverSidepots.map(sp => (
                                <button
                                  key={sp.id}
                                  onClick={() => handleRolloverEntry(entry, null, sp.id)}
                                  disabled={rolloverSaving}
                                  className="px-2 py-1 bg-[#161616] border border-[#262626] rounded text-[11px] text-white hover:border-[#FF9500] hover:bg-[#FF9500]/10 transition-colors disabled:opacity-40"
                                  data-testid={`rollover-to-sidepot-${sp.id}-${entry.id}`}
                                  title="Sidepot"
                                >
                                  <span className="px-1 py-0.5 bg-[#FF9500]/20 text-[#FF9500] rounded text-[9px] font-bold mr-1">SP</span>
                                  {sp.name}
                                </button>
                              ))}
                            </div>
                            <button
                              onClick={() => setRolloverEntry(null)}
                              className="px-2 py-1 text-[11px] text-[#9E9E9E] hover:text-white"
                              data-testid={`rollover-cancel-${entry.id}`}
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                      );
                    })()}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}
      </div>
      </div>

      {/* Edit Modal */}
      {editingEntry && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => setEditingEntry(null)}>
          <div className="bg-[#161616] border border-[#262626] rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="edit-entry-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>Edit Entry</h3>
              <button onClick={() => setEditingEntry(null)} className="p-1 hover:bg-[#262626] rounded"><X size={18} className="text-[#9E9E9E]" /></button>
            </div>
            <p className="text-[#9E9E9E] text-xs mb-4">Class: <span className="text-white">{editingEntry.class_name}</span></p>
            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-xs text-[#9E9E9E] mb-1">Rider Name *</label>
                <input
                  type="text"
                  value={editRider}
                  onChange={(e) => setEditRider(e.target.value)}
                  className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF]"
                  data-testid="edit-rider-name-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs text-[#9E9E9E] mb-1">Horse Name</label>
                <input
                  type="text"
                  value={editHorse}
                  onChange={(e) => setEditHorse(e.target.value)}
                  className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF]"
                  data-testid="edit-horse-name-input"
                />
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setEditingEntry(null)} className="px-4 py-2 rounded-md text-sm font-semibold text-[#9E9E9E] bg-[#262626] hover:bg-[#333] transition-colors" data-testid="edit-cancel-btn">Cancel</button>
              <button onClick={handleSaveEdit} className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#0096FF] hover:bg-[#0096FF]/80 transition-colors" data-testid="edit-save-btn">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (() => {
        // v235 — Context-aware copy so producers know exactly what will and
        // won't be removed. Rollover CHILD delete leaves the source alone;
        // rollover SOURCE delete cascades to every child.
        const isChild = Boolean(deleteConfirm.is_rollover);
        const srcClassName = isChild
          ? (show?.classes || []).find(c => c.id === deleteConfirm.rollover_source_class_id)?.name
          : null;
        const children = !isChild
          ? entries.filter(e => e.is_rollover && e.rollover_source_entry_id === deleteConfirm.id)
          : [];
        return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-[#161616] border border-[#262626] rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="delete-entry-modal">
            <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>
              {isChild ? 'Remove Rollover Entry' : 'Remove Entry'}
            </h3>
            <p className="text-[#9E9E9E] text-sm mb-1">
              Are you sure you want to remove <span className="text-white font-semibold">{deleteConfirm.rider_name}</span> from <span className="text-white font-semibold">{deleteConfirm.class_name}</span>?
            </p>
            {isChild && (
              <div className="mt-3 mb-3 px-3 py-2 rounded-md bg-[#34C759]/10 border border-[#34C759]/30" data-testid="delete-rollover-child-notice">
                <p className="text-[#34C759] text-xs font-semibold">
                  Rollover only — source entry is safe
                </p>
                <p className="text-[#9E9E9E] text-[11px] mt-0.5">
                  This is a rollover into <span className="text-white font-semibold">{deleteConfirm.class_name}</span>
                  {srcClassName ? <> from <span className="text-white font-semibold">{srcClassName}</span></> : null}.
                  Only this row will be removed — the original entry in the source class will stay.
                </p>
              </div>
            )}
            {!isChild && children.length > 0 && (
              <div className="mt-3 mb-3 px-3 py-2 rounded-md bg-[#FF9500]/10 border border-[#FF9500]/30" data-testid="delete-parent-with-children-notice">
                <p className="text-[#FF9500] text-xs font-semibold">
                  This entry has {children.length} rollover{children.length === 1 ? '' : 's'}
                </p>
                <p className="text-[#9E9E9E] text-[11px] mt-0.5">
                  The following rollover entries will also be removed:
                </p>
                <ul className="text-[#9E9E9E] text-[11px] mt-1 list-disc pl-4">
                  {children.slice(0, 4).map(c => (
                    <li key={c.id}><span className="text-white">{c.class_name}</span></li>
                  ))}
                  {children.length > 4 && <li>&hellip; and {children.length - 4} more</li>}
                </ul>
              </div>
            )}
            <p className="text-[#FF3B30]/80 text-xs mb-6">This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 rounded-md text-sm font-semibold text-[#9E9E9E] bg-[#262626] hover:bg-[#333] transition-colors" data-testid="delete-entry-cancel-btn">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#FF3B30] hover:bg-[#FF3B30]/80 transition-colors" data-testid="delete-entry-confirm-btn">
                {isChild ? 'Remove Rollover' : 'Remove Entry'}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Deny Refund Modal */}
      {denyModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => { setDenyModal(null); setDenyReason(''); }}>
          <div className="bg-[#161616] border border-[#262626] rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="deny-refund-modal">
            <h3 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'Outfit' }}>Deny Refund Request</h3>
            <div className="mb-4">
              <label className="block text-xs text-[#9E9E9E] mb-1">Reason (optional)</label>
              <textarea
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
                rows={2}
                placeholder="Provide a reason for denying..."
                className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm placeholder:text-[#555] focus:outline-none focus:border-[#0096FF]"
                data-testid="deny-reason-input"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setDenyModal(null); setDenyReason(''); }} className="px-4 py-2 rounded-md text-sm font-semibold text-[#9E9E9E] bg-[#262626] hover:bg-[#333] transition-colors">Cancel</button>
              <button onClick={handleDenyRefund} disabled={processingRefund === denyModal} className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#FF3B30] hover:bg-[#FF3B30]/80 transition-colors disabled:opacity-50" data-testid="deny-refund-confirm-btn">
                {processingRefund === denyModal ? 'Denying...' : 'Deny Refund'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Direct Refund Confirmation Modal */}
      {directRefundConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => setDirectRefundConfirm(null)}>
          <div className="bg-[#161616] border border-[#262626] rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="direct-refund-modal">
            <h3 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'Outfit' }}>Issue Direct Refund</h3>
            <p className="text-[#9E9E9E] text-sm mb-1">
              Refund all paid entries for <span className="text-white font-semibold">{directRefundConfirm.riderName}</span> in this show?
            </p>
            <p className="text-[#FF9500] text-xs mb-5">The refund will be returned to the rider's original payment method via Stripe.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDirectRefundConfirm(null)} className="px-4 py-2 rounded-md text-sm font-semibold text-[#9E9E9E] bg-[#262626] hover:bg-[#333] transition-colors">Cancel</button>
              <button onClick={handleDirectRefund} disabled={processingRefund === directRefundConfirm.riderId} className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#FF9500] hover:bg-[#FF9500]/80 transition-colors disabled:opacity-50" data-testid="direct-refund-confirm-btn">
                {processingRefund === directRefundConfirm.riderId ? 'Processing...' : 'Process Refund'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Class Picker Modal */}
      {importFile && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => { setImportFile(null); setImportClassId(null); }}>
          <div className="bg-[#161616] border border-[#262626] rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="import-class-picker-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white" style={{fontFamily: 'Outfit'}}>Import Entries</h3>
              <button onClick={() => { setImportFile(null); setImportClassId(null); }} className="p-1 hover:bg-[#262626] rounded"><X size={18} className="text-[#9E9E9E]" /></button>
            </div>
            <p className="text-[#9E9E9E] text-sm mb-1">File: <span className="text-white font-semibold">{importFile.name}</span></p>
            <p className="text-[#555] text-xs mb-4">
              Select which class to import entries into:
              {activeDayTab !== 'ALL' && (
                <span className="text-[#0096FF] ml-1">(showing {activeDayTab} only)</span>
              )}
            </p>
            <div className="space-y-1.5 max-h-60 overflow-y-auto mb-4" data-testid="import-class-list">
              {(activeDayTab === 'ALL'
                ? show.classes
                : show.classes.filter(c => (classIdsByDay[activeDayTab] || []).includes(c.id))
              ).map(cls => (
                <label
                  key={cls.id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                    importClassId === cls.id ? 'bg-[#0096FF]/10 border border-[#0096FF]' : 'bg-[#0A0A0A] border border-[#262626] hover:border-[#0096FF]/30'
                  }`}
                  data-testid={`import-class-option-${cls.id}`}
                >
                  <input
                    type="radio"
                    name="importClass"
                    value={cls.id}
                    checked={importClassId === cls.id}
                    onChange={() => setImportClassId(cls.id)}
                    className="accent-[#0096FF]"
                  />
                  <div>
                    <span className="text-white text-sm font-semibold">{cls.name}</span>
                    {cls.class_type && cls.class_type !== 'standard' && (
                      <span className={`ml-2 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${cls.class_type === 'warmup' ? 'bg-[#34C759]/15 text-[#34C759]' : 'bg-[#34C759]/15 text-[#34C759]'}`}>{cls.class_type === 'warmup' ? 'WARM-UP' : 'EXHIBITION'}</span>
                    )}
                    {cls.scheduled_day && <span className="text-[#0096FF] text-xs ml-2">{cls.scheduled_day}</span>}
                  </div>
                </label>
              ))}
            </div>
            <p className="text-[#555] text-[10px] mb-4">Format: Rider Name | Horse Name | Time (optional). Headers are optional.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setImportFile(null); setImportClassId(null); }} className="px-4 py-2 rounded-md text-sm font-semibold text-[#9E9E9E] bg-[#262626] hover:bg-[#333] transition-colors" data-testid="import-cancel-btn">Cancel</button>
              <button
                onClick={handleImportConfirm}
                disabled={!importClassId || importLoading}
                className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#0096FF] hover:bg-[#00D4FF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="import-confirm-btn"
              >
                {importLoading ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Entry Modal (multi-class) */}
      {addModal && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => !addSubmitting && setAddModal(false)}
          data-testid="add-entry-modal"
        >
          <div
            className="bg-[#111] border border-[#262626] rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Add Entry</h3>
                <p className="text-xs text-[#9E9E9E] mt-0.5">Add a rider to one or more classes in a single submission.</p>
              </div>
              <button
                onClick={() => !addSubmitting && setAddModal(false)}
                className="text-[#9E9E9E] hover:text-white transition-colors"
                data-testid="add-entry-close"
              >
                <X size={18} weight="bold" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs text-[#9E9E9E] mb-1.5 font-semibold">Rider Name *</label>
                <RiderAutocomplete
                  value={addRider}
                  onChange={setAddRider}
                  onHorseChange={(h) => setAddHorse(h)}
                  showId={id}
                  placeholder="e.g. Sarah Johnson"
                  autoFocus
                  testId="add-entry-rider"
                />
              </div>
              <div>
                <label className="block text-xs text-[#9E9E9E] mb-1.5 font-semibold">Horse Name</label>
                <input
                  value={addHorse}
                  onChange={(e) => setAddHorse(e.target.value)}
                  placeholder="e.g. Lightning"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF]"
                  data-testid="add-entry-horse"
                />
              </div>
            </div>

            <label className="block text-xs text-[#9E9E9E] mb-2 font-semibold">
              Classes <span className="text-[#FF3B30]">*</span>
              <span className="text-[#555] font-normal ml-1">(pick one or more)</span>
              {activeDayTab !== 'ALL' && (
                <span className="text-[#0096FF] font-normal ml-1">— filtered to {activeDayTab}</span>
              )}
            </label>

            {(() => {
              // Group classes by scheduled day for easier scanning. When the
              // producer has a day tab active we narrow this picker to that
              // day too so they don't have to re-scan all 14 classes mid-show.
              const sourceClasses = activeDayTab === 'ALL'
                ? (show?.classes || [])
                : (show?.classes || []).filter(c => (classIdsByDay[activeDayTab] || []).includes(c.id));
              const byDay = {};
              sourceClasses.forEach((c) => {
                const day = c.scheduled_day
                  || (c.scheduled_date ? new Date(`${String(c.scheduled_date).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }) : 'Unscheduled');
                if (!byDay[day]) byDay[day] = [];
                byDay[day].push(c);
              });
              const dayKeysLocal = Object.keys(byDay);
              return (
                <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 mb-4 max-h-[320px] overflow-y-auto" data-testid="add-entry-class-picker">
                  {dayKeysLocal.map((day) => (
                    <div key={day} className="mb-3 last:mb-0">
                      {dayKeysLocal.length > 1 && (
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-bold">{day}</p>
                          <button
                            type="button"
                            onClick={() => {
                              const dayIds = byDay[day].map((c) => c.id);
                              const allPicked = dayIds.every((cid) => addClassIds.has(cid));
                              setAddClassIds((prev) => {
                                const next = new Set(prev);
                                if (allPicked) dayIds.forEach((cid) => next.delete(cid));
                                else dayIds.forEach((cid) => next.add(cid));
                                return next;
                              });
                            }}
                            className="text-[10px] text-[#0096FF] hover:text-[#00D4FF] font-semibold"
                            data-testid={`add-entry-day-toggle-${day}`}
                          >
                            {byDay[day].every((c) => addClassIds.has(c.id)) ? 'Clear day' : 'Select day'}
                          </button>
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {byDay[day].map((c) => {
                          const checked = addClassIds.has(c.id);
                          const opts = addClassOpts[c.id] || {};
                          // Destination classes this SOURCE can roll over TO.
                          // A class D is a rollover-destination for c if D lists
                          // c.id in its rollover_from_class_ids (i.e., D accepts
                          // rollovers coming from c). Matches the rider-side flow.
                          // v264 — Also restrict to SAME-DAY destinations so
                          // a Saturday source can't roll into a Sunday class.
                          const rolloverDestinations = (show?.classes || [])
                            .filter((d) => d.id !== c.id)
                            .filter((d) => {
                              const src = d.rollover_from_class_ids?.length
                                ? d.rollover_from_class_ids
                                : (d.rollover_from_class_id ? [d.rollover_from_class_id] : []);
                              return src.includes(c.id) && isSameDay(c, d);
                            })
                            .concat((show?.sidepots || [])
                              .filter((sp) => sp.id && isSameDay(c, sp))
                              .map((sp) => ({ ...sp, isSidepot: true })));
                          const selectedDests = opts.rolloverDestClassIds || [];
                          return (
                            <div
                              key={c.id}
                              className={`rounded transition-colors ${checked ? 'bg-[#0096FF]/10 border border-[#0096FF]/40' : 'bg-[#161616] border border-transparent hover:border-[#262626]'}`}
                              data-testid={`add-entry-class-option-${c.id}`}
                            >
                              <label className="flex items-center gap-2 px-2.5 py-2 cursor-pointer text-xs">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleAddClass(c.id)}
                                  className="w-3.5 h-3.5 accent-[#0096FF] flex-shrink-0"
                                />
                                <span className="text-white truncate flex-1">{c.name}</span>
                                {c.class_type && c.class_type !== 'standard' && (
                                  <span className="px-1.5 py-0.5 bg-[#34C759]/15 text-[#34C759] rounded text-[9px] font-bold flex-shrink-0 uppercase">
                                    {c.class_type === 'warmup' ? 'Warm-up' : 'Exhibition'}
                                  </span>
                                )}
                                {c.entry_fee > 0 && <span className="text-[#34C759] text-[10px] font-bold flex-shrink-0">${c.entry_fee}</span>}
                              </label>
                              {checked && (
                                <div className="px-2.5 pb-2 pt-0.5" onClick={(e) => e.stopPropagation()}>
                                  <div className="flex items-center gap-3 flex-wrap">
                                    <label className="flex items-center gap-1 text-[10px] text-[#9E9E9E] uppercase tracking-wide font-semibold flex-shrink-0">
                                      Draw #
                                      <input
                                        type="number"
                                        min={1}
                                        value={opts.drawNumber || ''}
                                        onChange={(e) => setClassOpt(c.id, { drawNumber: e.target.value })}
                                        placeholder="–"
                                        className="w-14 px-1.5 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs text-center focus:outline-none focus:border-[#0096FF]"
                                        data-testid={`add-entry-draw-${c.id}`}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </label>
                                    {['warmup','exhibition'].includes(c.class_type) && (
                                      <label
                                        className="flex items-center gap-1 text-[10px] text-[#34C759] uppercase tracking-wide font-bold flex-shrink-0"
                                        data-testid={`add-entry-runs-row-${c.id}`}
                                      >
                                        Runs
                                        <select
                                          value={opts.runs || 1}
                                          onChange={(e) => setClassOpt(c.id, { runs: parseInt(e.target.value, 10) || 1 })}
                                          onClick={(e) => e.stopPropagation()}
                                          className="px-1.5 py-1 bg-[#0A0A0A] border border-[#34C759]/40 rounded text-white text-xs focus:outline-none focus:border-[#34C759]"
                                          data-testid={`add-entry-runs-${c.id}`}
                                        >
                                          {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
                                        </select>
                                      </label>
                                    )}
                                    {rolloverDestinations.length > 0 ? (
                                      <div className="flex items-center gap-2 flex-wrap" data-testid={`add-entry-rollover-row-${c.id}`}>
                                        <span className="text-[10px] text-[#FF9500] uppercase tracking-wide font-semibold flex-shrink-0">Roll into</span>
                                        <div className="flex flex-wrap gap-x-2 gap-y-1">
                                          {rolloverDestinations.map((dest) => {
                                            const active = selectedDests.includes(dest.id);
                                            return (
                                              <label
                                                key={dest.id}
                                                className={`flex items-center gap-1 text-[11px] cursor-pointer select-none ${active ? 'text-[#FF9500] font-semibold' : 'text-[#9E9E9E] hover:text-white'}`}
                                                data-testid={`add-entry-rollover-to-${c.id}-${dest.id}`}
                                              >
                                                <input
                                                  type="checkbox"
                                                  checked={active}
                                                  onChange={(e) => {
                                                    e.stopPropagation();
                                                    const next = active
                                                      ? selectedDests.filter((x) => x !== dest.id)
                                                      : [...selectedDests, dest.id];
                                                    setClassOpt(c.id, { rolloverDestClassIds: next });
                                                  }}
                                                  onClick={(e) => e.stopPropagation()}
                                                  className="w-3 h-3 accent-[#FF9500] flex-shrink-0"
                                                />
                                                {dest.isSidepot && <span className="px-1 py-0.5 bg-[#FF9500]/20 text-[#FF9500] rounded text-[9px] font-bold">SP</span>}
                                                <span>{dest.name}{dest.scheduled_day ? ` (${dest.scheduled_day})` : ''}</span>
                                              </label>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-[9px] text-[#555] italic" data-testid={`add-entry-no-rollover-${c.id}`}>
                                        No rollover targets
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Target Race opt-in — mirrors the rider self-entry form. Only
                paid targets show here; producers opt the rider into one or
                more target races, picking ONE category per multi-category
                target (per v102 spec). Synthetic rider_id links these opt-ins
                back to the producer-created entries during results compute. */}
            {targets.filter(t => Number(t.entry_fee) > 0).length > 0 && (
              <div className="mt-2 mb-4 bg-[#0A0A0A] border border-[#FF375F]/30 rounded-lg p-3" data-testid="add-entry-target-section">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[#FF375F]">&#9678;</span>
                  <h4 className="text-sm font-bold text-white" style={{ fontFamily: 'Outfit' }}>Target Race Opt-In</h4>
                  <span className="text-[10px] text-[#9E9E9E]">Optional — collect fee at the office</span>
                </div>
                <div className="space-y-1.5">
                  {targets.filter(t => Number(t.entry_fee) > 0).map((tgt) => {
                    const checked = addSelectedTargetIds.includes(tgt.id);
                    const cats = tgt.categories || [];
                    const eligibleCats = cats.filter((c) =>
                      (c.class_ids || []).some((cid) => addClassIds.has(cid))
                    );
                    const defaultCat = (eligibleCats[0] || cats[0])?.id;
                    const picks = addTargetCategoryPicks[tgt.id] || (defaultCat ? [defaultCat] : []);
                    const toggleTarget = () => {
                      setAddSelectedTargetIds((prev) =>
                        checked ? prev.filter((tid) => tid !== tgt.id) : [...prev, tgt.id]
                      );
                      if (!checked && defaultCat) {
                        setAddTargetCategoryPicks((prev) => ({ ...prev, [tgt.id]: [defaultCat] }));
                      }
                    };
                    const selectCat = (catId) => {
                      setAddTargetCategoryPicks((prev) => ({ ...prev, [tgt.id]: [catId] }));
                    };
                    return (
                      <div
                        key={tgt.id}
                        className={`rounded transition-colors ${
                          checked
                            ? 'bg-[#FF375F]/10 border border-[#FF375F]/40'
                            : 'bg-[#161616] border border-transparent hover:border-[#262626]'
                        }`}
                        data-testid={`add-entry-target-row-${tgt.id}`}
                      >
                        <label className="flex items-start gap-2 px-2.5 py-2 cursor-pointer text-xs" data-testid={`add-entry-target-toggle-${tgt.id}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={toggleTarget}
                            className="mt-0.5 w-3.5 h-3.5 accent-[#FF375F] flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-semibold truncate">{tgt.name}</p>
                            <p className="text-[10px] text-[#9E9E9E]">
                              {cats.length} categor{cats.length !== 1 ? 'ies' : 'y'} &middot; {tgt.places_paid} place{tgt.places_paid !== 1 ? 's' : ''} paid
                            </p>
                          </div>
                          <span className="text-[#34C759] font-bold text-xs flex-shrink-0">+${Number(tgt.entry_fee).toFixed(0)}</span>
                        </label>
                        {checked && cats.length > 1 && (
                          <div className="px-2.5 pb-2 pt-0 border-t border-[#FF375F]/20" data-testid={`add-entry-target-cats-${tgt.id}`}>
                            <p className="text-[9px] text-[#9E9E9E] mb-1 uppercase tracking-wider mt-1.5">Choose ONE category</p>
                            <div className="grid grid-cols-2 gap-1">
                              {cats.map((cat) => {
                                const catSelected = picks[0] === cat.id;
                                const enteredInCat = (cat.class_ids || []).filter((cid) => addClassIds.has(cid)).length;
                                return (
                                  <label
                                    key={cat.id}
                                    className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer transition-colors text-[11px] ${
                                      catSelected
                                        ? 'bg-[#FF375F]/15 border border-[#FF375F]/40'
                                        : 'bg-[#0A0A0A] border border-[#262626] hover:border-[#FF375F]/30'
                                    }`}
                                    data-testid={`add-entry-target-cat-${tgt.id}-${cat.id}`}
                                  >
                                    <input
                                      type="radio"
                                      name={`add-target-cat-${tgt.id}`}
                                      checked={catSelected}
                                      onChange={() => selectCat(cat.id)}
                                      className="w-3 h-3 accent-[#FF375F] flex-shrink-0"
                                    />
                                    <span className="flex-1 text-white truncate">{cat.name}</span>
                                    <span className="text-[9px] text-[#9E9E9E] flex-shrink-0">
                                      {enteredInCat > 0 ? `${enteredInCat}/${(cat.class_ids || []).length}` : `${(cat.class_ids || []).length}`}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-[#9E9E9E]" data-testid="add-entry-summary">
                {addClassIds.size === 0 ? 'No classes selected' : `${addClassIds.size} class${addClassIds.size === 1 ? '' : 'es'} selected`}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setAddModal(false)}
                  disabled={addSubmitting}
                  className="px-4 py-2 bg-[#262626] text-[#9E9E9E] text-sm font-semibold rounded-md hover:bg-[#333] disabled:opacity-50"
                  data-testid="add-entry-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddEntries}
                  disabled={addSubmitting || !addRider.trim() || addClassIds.size === 0}
                  className="px-5 py-2 bg-[#0096FF] text-white text-sm font-bold rounded-md hover:bg-[#0096FF]/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="add-entry-submit"
                >
                  {addSubmitting
                    ? 'Adding...'
                    : `Add to ${addClassIds.size || 0} Class${addClassIds.size === 1 ? '' : 'es'}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show Revenue Report modal */}
      {reportOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setReportOpen(false)} data-testid="revenue-report-modal">
          <div className="bg-[#0F0F0F] border border-[#262626] rounded-lg w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#262626] sticky top-0 bg-[#0F0F0F] z-10">
              <div className="flex items-center gap-2">
                <ChartBar size={20} className="text-[#FF9500]" />
                <h2 className="text-base sm:text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>Show Revenue Report</h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={refreshReport}
                  disabled={reportLoading}
                  className="px-3 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-[11px] text-[#9E9E9E] hover:text-white disabled:opacity-50"
                  data-testid="revenue-report-refresh"
                >
                  {reportLoading ? 'Loading…' : 'Refresh'}
                </button>
                {/* v228 — CSV + PDF exports. Uses blob download so the
                    JWT auth header rides along. */}
                <button
                  onClick={async () => {
                    try {
                      const r = await api.get(`/shows/${id}/revenue-report.csv`, { responseType: 'blob' });
                      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
                      const a = document.createElement('a');
                      a.href = url; a.download = 'revenue-report.csv';
                      document.body.appendChild(a); a.click(); a.remove();
                      URL.revokeObjectURL(url);
                    } catch { toast.error('CSV export failed'); }
                  }}
                  className="px-3 py-1 bg-[#00D4FF]/15 border border-[#00D4FF]/40 rounded text-[11px] text-[#00D4FF] hover:bg-[#00D4FF]/25 font-bold"
                  data-testid="revenue-report-csv-btn"
                >CSV</button>
                <button
                  onClick={async () => {
                    try {
                      const r = await api.get(`/shows/${id}/revenue-report.pdf`, { responseType: 'blob' });
                      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                      const a = document.createElement('a');
                      a.href = url; a.download = 'revenue-report.pdf';
                      document.body.appendChild(a); a.click(); a.remove();
                      URL.revokeObjectURL(url);
                    } catch { toast.error('PDF export failed'); }
                  }}
                  className="px-3 py-1 bg-[#0096FF]/15 border border-[#0096FF]/40 rounded text-[11px] text-[#0096FF] hover:bg-[#0096FF]/25 font-bold"
                  data-testid="revenue-report-pdf-btn"
                >PDF</button>
                <button onClick={() => setReportOpen(false)} className="p-1 text-[#9E9E9E] hover:text-white" data-testid="revenue-report-close">
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="p-5 space-y-3">
              {routingDiag && (
                <SquareRoutingBanner diag={routingDiag} />
              )}
              {reportLoading && !reportData ? (
                <p className="text-[#9E9E9E] text-sm">Loading report…</p>
              ) : reportData ? (
                <>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-3">
                      <p className="text-[10px] text-[#9E9E9E]">Total Entries</p>
                      <p className="text-xl font-bold text-white" data-testid="report-total-entries">{reportData.entries.total}</p>
                      <p className="text-[10px] text-[#34C759]">{reportData.entries.paid} paid</p>
                    </div>
                    <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-3">
                      <p className="text-[10px] text-[#9E9E9E]">Rollovers</p>
                      <p className="text-xl font-bold text-white">{reportData.entries.rollovers}</p>
                    </div>
                    <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-3">
                      <p className="text-[10px] text-[#9E9E9E]">Practice Runs</p>
                      <p className="text-xl font-bold text-white">{reportData.entries.practice}</p>
                    </div>
                  </div>

                  <ReportRow label="Class Entry Fees" detail={`${reportData.entries.total} entries (incl. rollovers/practice)`} amount={reportData.entry_fees.total} testid="report-entry-fees" />
                  {reportData.sidepots.total > 0 && (
                    <ReportRow label="Sidepots" detail={`${reportData.sidepots.count} sidepot opt-ins`} amount={reportData.sidepots.total} testid="report-sidepots" />
                  )}
                  {reportData.targets.total > 0 && (
                    <ReportRow label="Target Race" detail={`${reportData.targets.count} opt-ins`} amount={reportData.targets.total} testid="report-targets" />
                  )}

                  <div className="text-[10px] text-[#555] uppercase tracking-wider mt-4 mb-1">Accommodations</div>
                  <ReportRow
                    label="Stalls"
                    detail={`${reportData.stalls.count} sold × $${reportData.stalls.unit_price || reportData.stalls.unit_price_per_day}`}
                    amount={reportData.stalls.total}
                    testid="report-stalls"
                  />
                  <ReportRow
                    label="RV Hookups"
                    detail={`${reportData.rv.count} sold × $${reportData.rv.unit_price || reportData.rv.unit_price_per_day}`}
                    amount={reportData.rv.total}
                    testid="report-rv"
                  />

                  {(reportData.tshirts.enabled || reportData.tie_out.enabled || reportData.shavings.enabled) && (
                    <div className="text-[10px] text-[#555] uppercase tracking-wider mt-4 mb-1">Add-Ons</div>
                  )}
                  {reportData.tshirts.enabled && (
                    <ReportRow
                      label={reportData.tshirts.label}
                      detail={`${reportData.tshirts.qty_sold} sold × $${reportData.tshirts.unit_price}`}
                      amount={reportData.tshirts.total}
                      testid="report-tshirts"
                    />
                  )}
                  {reportData.tie_out.enabled && (
                    <ReportRow
                      label={reportData.tie_out.label}
                      detail={`${reportData.tie_out.count} × $${reportData.tie_out.unit_price}`}
                      amount={reportData.tie_out.total}
                      testid="report-tie-out"
                    />
                  )}
                  {reportData.shavings.enabled && (
                    <ReportRow
                      label={reportData.shavings.label}
                      detail={`${reportData.shavings.qty_sold} bags × $${reportData.shavings.unit_price}`}
                      amount={reportData.shavings.total}
                      testid="report-shavings"
                    />
                  )}

                  <div className="border-t border-[#262626] mt-4 pt-3 flex items-center justify-between">
                    <span className="text-base font-bold text-white">Grand Total</span>
                    <span className="text-2xl font-black text-[#34C759]" data-testid="report-grand-total">${reportData.grand_total.toFixed(2)}</span>
                  </div>
                  <p className="text-[10px] text-[#555] mt-2">
                    Counts ALL entries / reservations including UNPAID. Paid-only entry fees: <span className="text-white">${reportData.entry_fees.paid_total.toFixed(2)}</span>.
                  </p>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
      {/* v228 — Balance Owed modal */}
      {balanceOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setBalanceOpen(false)} data-testid="balance-owed-modal">
          <div className="bg-[#0F0F0F] border border-[#262626] rounded-lg w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#262626] sticky top-0 bg-[#0F0F0F] z-10">
              <div>
                <h2 className="text-base sm:text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>Balance Owed</h2>
                {balanceData && (
                  <p className="text-[11px] text-[#9E9E9E]">
                    {balanceData.riders_count} rider{balanceData.riders_count !== 1 ? 's' : ''} · <span className="text-[#FF3B30] font-bold">${balanceData.total_owed.toFixed(2)}</span> outstanding
                  </p>
                )}
              </div>
              <button onClick={() => setBalanceOpen(false)} className="p-1 text-[#9E9E9E] hover:text-white" data-testid="balance-owed-close">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-2">
              {balanceLoading && !balanceData && <p className="text-[#9E9E9E] text-sm">Loading…</p>}
              {balanceData && balanceData.riders.length === 0 && (
                <p className="text-[#34C759] text-sm">🎉 Everyone&apos;s paid up. No outstanding balances.</p>
              )}
              {balanceData && balanceData.riders.map((r) => {
                const smsBody = encodeURIComponent(
                  `Hi ${r.rider_name}, this is a reminder that you owe $${r.amount_owed.toFixed(2)} for ${balanceData.show_name}. Pay online at your entry link. Thanks!`
                );
                const emailSubject = encodeURIComponent(`Payment reminder — ${balanceData.show_name}`);
                const emailBody = encodeURIComponent(
                  `Hi ${r.rider_name},\n\nJust a quick reminder that you have an outstanding balance of $${r.amount_owed.toFixed(2)} for ${balanceData.show_name}. Please complete your payment when you have a chance.\n\nThanks!`
                );
                return (
                  <div key={r.rider_id || r.rider_name}
                       className="bg-[#0A0A0A] border border-[#262626] rounded-lg px-4 py-3"
                       data-testid={`balance-row-${r.rider_id || r.rider_name}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <p className="text-sm font-semibold text-white">{r.rider_name}</p>
                        <p className="text-[10px] text-[#9E9E9E]">
                          {r.entries.length} unpaid item{r.entries.length !== 1 ? 's' : ''}
                          {r.email && ` · ${r.email}`}
                          {r.phone && ` · ${r.phone}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-bold text-[#FF3B30] tabular-nums">${r.amount_owed.toFixed(2)}</p>
                        {r.phone && (
                          <a href={`sms:${r.phone}?&body=${smsBody}`}
                             className="px-2 py-1 bg-[#34C759]/15 border border-[#34C759]/40 rounded text-[11px] text-[#34C759] font-bold hover:bg-[#34C759]/25"
                             data-testid={`balance-sms-${r.rider_id || r.rider_name}`}>
                            Text
                          </a>
                        )}
                        {r.email && (
                          <a href={`mailto:${r.email}?subject=${emailSubject}&body=${emailBody}`}
                             className="px-2 py-1 bg-[#0096FF]/15 border border-[#0096FF]/40 rounded text-[11px] text-[#0096FF] font-bold hover:bg-[#0096FF]/25"
                             data-testid={`balance-email-${r.rider_id || r.rider_name}`}>
                            Email
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 text-[10px] text-[#9E9E9E] space-y-0.5">
                      {r.entries.map((e) => (
                        <p key={e.entry_id}>· {e.class_name} — ${e.fee.toFixed(2)}</p>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ReportRow = ({ label, detail, amount, testid }) => (
  <div className="flex items-center justify-between bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2.5" data-testid={testid}>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-white truncate">{label}</p>
      <p className="text-[10px] text-[#9E9E9E] truncate">{detail}</p>
    </div>
    <p className="text-base font-bold text-white tabular-nums whitespace-nowrap ml-3">${Number(amount || 0).toFixed(2)}</p>
  </div>
);

/**
 * Square Routing banner — shown at the top of the Revenue Report modal so
 * producers can see at a glance which Square account is receiving payments
 * for this show, and (if it's NOT splitting correctly) what to do about it.
 */
const SquareRoutingBanner = ({ diag }) => {
  const ok = diag.verdict === 'OK';
  const color = ok ? '#34C759' : '#FF9500';
  const label = {
    OK: 'Routing to Producer · Platform fee splitting correctly',
    NOT_CONNECTED: 'Going to Master · Producer not connected to Square',
    ROUTING_DISABLED: 'Going to Master · Square routing OFF for this show',
    MISSING_MARKETPLACE_SCOPE: 'Going to Producer · Platform fee NOT splitting (reconnect needed)',
    ROUTING_ON_BUT_LAST_TX_FELL_BACK: 'Last payment fell back to Master',
  }[diag.verdict] || diag.verdict;

  return (
    <div
      className="rounded-lg p-3 mb-3 border"
      style={{ borderColor: `${color}40`, background: `${color}10` }}
      data-testid="square-routing-banner"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="w-2 h-2 rounded-full" style={{ background: color }} />
        <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color }} data-testid="square-routing-verdict">
          {label}
        </span>
      </div>
      <p className="text-[11px] text-[#C8C8C8] leading-relaxed">{diag.next_step}</p>
      {diag.last_transaction && (
        <p className="text-[10px] text-[#9E9E9E] mt-2 font-mono" data-testid="square-routing-last-tx">
          Last tx: ${Number(diag.last_transaction.amount || 0).toFixed(2)}
          {' · '}routed_to_producer={String(diag.last_transaction.routed_to_producer)}
          {' · '}app_fee=${(Number(diag.last_transaction.app_fee_cents || 0) / 100).toFixed(2)}
        </p>
      )}
    </div>
  );
};

export default ManageEntries;
